// src/utils/labelNumbering.js
// ─────────────────────────────────────────────────────────────────────────────
// OUTLINE NUMBERING FOR NESTED FORM FIELDS.
//
// These forms are enumerated legal documents. An entry asks for (a) Scientific
// Name, (b) Microorganisms isolated from, (c) Source of access, and then (d)
// Geographical location — which is not a field but a BLOCK, and the template
// opens the block's own list at (a) again:
//
//     (a) Scientific Name
//     (b) Microorganisms isolated from
//     (c) Source of access
//     (d) Geographical location from where the microorganism was collected
//         (a) Village / Panchayat      ← collides with the (a) above
//         (b) Town / Taluk             ← collides with the (b) above
//         (c) District                 ← collides with the (c) above
//         (d) State                    ← collides with the (d) it sits under
//
// On the form the indent and the block's heading say where the inner list
// begins. Everywhere the entry is FLATTENED — the saved-entry card, the
// preview — both are gone, and the reader is left with two (a)s, two
// (b)s and two (c)s in one run, "(c) District" beside "(c) Source of access" as
// though they were the same list.
//
// The fix is the one every legal document already uses: each depth has its OWN
// style, so a level can never be mistaken for the one above it.
//
//     1.  2.  3.        →   (a) (b) (c)   →   (i) (ii) (iii)   →   (A) (B) (C)
//
// A block's children are therefore renumbered into the style BELOW their
// parent's, in template order:
//
//     (d) Geographical location from where the microorganism was collected
//         (i)   Village / Panchayat
//         (ii)  Town / Taluk
//         (iii) District
//         (iv)  State
//
// Position, not the template's own letter, is what carries over. Rewriting the
// letter ("(d)" under "(d)") is exactly the case a marker-preserving scheme gets
// wrong, and it is the common one: an inner list nearly always starts at (a),
// so its fourth item nearly always collides with the (d) it lives under.
//
// A child the template did not enumerate is left alone — this restyles the
// numbering a form already has, and never invents numbering it does not.
//
// Pure text over labels. No markup, no template reads.
// ─────────────────────────────────────────────────────────────────────────────

/* The enumerator shapes a label opens with. Order matters: the compound shape
   ("d(a)", left by an earlier scheme) must be tried before the plain one. */
const COMPOUND_RE = /^\s*(?:[0-9]{1,3}|[A-Za-z])(?:\(\s*[^\s()]{1,5}\s*\))+\s*/;
const PAREN_RE    = /^\s*\(\s*([^\s()]{1,5})\s*\)\s*/;   // (d)  (iv)  (12)
const PLAIN_RE    = /^\s*([0-9]{1,3}|[A-Za-z])\s*[).]\s*/; // 4.   d)

const ROMAN_RE = /^[ivxlcdm]+$/i;
const ROMAN_VALUES = [
  [1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'], [100, 'c'], [90, 'xc'],
  [50, 'l'], [40, 'xl'], [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i'],
];

/**
 * The enumerator a label opens with, and the text after it.
 *
 * Deliberately strict: a bracketed token, or a single letter / up to three
 * digits closed by a bracket or full stop. "No. of samples" and "5 kg per batch"
 * are labels, not enumerated items, and must come back untouched.
 *
 * @param {string} label
 * @returns {{marker:string, text:string}} `marker` is '' when the label carries
 *          no enumerator, and `text` is then the whole label.
 */
export function splitLabelMarker(label) {
  const raw = String(label ?? '').trim();
  if (!raw) return { marker: '', text: '' };
  const compound = raw.match(COMPOUND_RE);
  if (compound) return { marker: compound[0].replace(/\s+/g, ''), text: raw.slice(compound[0].length).trim() };
  for (const re of [PAREN_RE, PLAIN_RE]) {
    const m = raw.match(re);
    if (m) return { marker: String(m[1]).trim(), text: raw.slice(m[0].length).trim() };
  }
  return { marker: '', text: raw };
}

/** A label with its enumerator removed — "4. Name of the …" → "Name of the …". */
export const labelText = label => splitLabelMarker(label).text || String(label ?? '').trim();

/* ── The style ladder ──────────────────────────────────────────────────────
   Each depth's style is decided by its parent's, so a form that numbers its
   sections 1. 2. 3. gets (a) (b) (c) beneath, then (i) (ii) (iii), then (A) (B)
   (C), then back to numbers. Four distinct styles is deeper than any of these
   forms nests. */
const STYLE_ORDER = ['number', 'lower-alpha', 'lower-roman', 'upper-alpha'];

/**
 * Which style is this marker written in?
 *
 * A SINGLE letter always reads as alphabetic, never as roman: "(d)" in a list
 * that ran (a) (b) (c) is the fourth item, not five hundred. Only a multi-letter
 * roman token ("ii", "iv", "xii") is read as roman.
 */
export function markerStyle(marker) {
  const m = String(marker ?? '').trim();
  if (!m) return '';
  if (/^[0-9]+$/.test(m)) return 'number';
  if (m.length > 1 && ROMAN_RE.test(m)) return m === m.toLowerCase() ? 'lower-roman' : 'upper-roman';
  if (/^[a-z]$/.test(m)) return 'lower-alpha';
  if (/^[A-Z]$/.test(m)) return 'upper-alpha';
  return '';
}

/** The style one level in from `parentStyle`. */
function childStyle(parentStyle) {
  const i = STYLE_ORDER.indexOf(parentStyle === 'upper-roman' ? 'lower-roman' : parentStyle);
  if (i === -1) return '';
  return STYLE_ORDER[(i + 1) % STYLE_ORDER.length];
}

const toRoman = n => {
  let out = '';
  let left = n;
  for (const [value, numeral] of ROMAN_VALUES) {
    while (left >= value) { out += numeral; left -= value; }
  }
  return out;
};

/* 1 → a, 26 → z, 27 → aa — a list longer than the alphabet still numbers. */
const toAlpha = n => {
  let out = '';
  let left = n;
  while (left > 0) {
    const rem = (left - 1) % 26;
    out = String.fromCharCode(97 + rem) + out;
    left = Math.floor((left - 1) / 26);
  }
  return out;
};

/** The n-th marker (1-based) in a style: formatMarker('lower-roman', 4) → 'iv'. */
export function formatMarker(style, n) {
  if (!(n > 0)) return '';
  switch (style) {
    case 'number':      return String(n);
    case 'lower-alpha': return toAlpha(n);
    case 'upper-alpha': return toAlpha(n).toUpperCase();
    case 'lower-roman': return toRoman(n);
    case 'upper-roman': return toRoman(n).toUpperCase();
    default:            return '';
  }
}

/**
 * Every enumerated child of a numbered block, renumbered one level in.
 *
 * Only the children the template ALREADY enumerated take part: they are numbered
 * 1, 2, 3… in template order and rendered in the style below the parent's. A
 * child with no enumerator keeps its label exactly, and so does every child of a
 * parent that has no enumerator of its own.
 *
 * Non-destructive: a field whose label does not change comes back as the SAME
 * object, and the array itself is returned unchanged when nothing does — so
 * nothing downstream comparing definitions by identity is disturbed.
 *
 * @param {string} parentLabel  the block's own label / sectionName
 * @param {Array}  fields       its child field definitions, in template order
 */
/* Would this style, over `count` items, ever produce the parent's own marker?
   The case that matters is a parent marked "(i)": read as a letter it is the
   ninth item of an (a)(b)(c)… list — Form E's address block really does reach
   "(i) Mobile Number" — and read as a numeral it is the first of an (i)(ii)(iii)
   list. Nothing in the label says which, so a roman child level under it would
   open with the very token its parent carries. Rather than guess the parent's
   intent, decline the style that collides and take the next one on the ladder. */
const styleCollides = (style, marker, count) => {
  if (!marker) return false;
  for (let n = 1; n <= count; n += 1) if (formatMarker(style, n) === marker) return true;
  return false;
};

export function withNestedLabels(parentLabel, fields) {
  const list = Array.isArray(fields) ? fields : [];
  if (!list.length) return list;

  /* ALREADY COMPOSED. A list carrying a `sectionTitle` marker is not one block's
     children — it is several blocks flattened into one run, each already
     numbered under the heading that still stands above it. Numbering the run as
     a single list restarts nothing and collides with everything: Form E's
     Institution page 1 became one (a)…(z)(aa) sequence spanning "(i) Complete
     address" and "(ii) Details of authorized representative", so the filed
     document numbered the representative's Title "(l)" while the form the
     applicant filled called it "(A)". Composition is the builders' business;
     this only numbers siblings. */
  if (list.some(f => f && f.sectionTitle && !f.name)) return list;

  const parentMarker = splitLabelMarker(parentLabel).marker;
  const enumerated = list.filter(f => f && f.label && splitLabelMarker(f.label).marker).length;
  if (!enumerated) return list;

  let style = childStyle(markerStyle(parentMarker));
  if (!style) return list;
  // At most one step per style on the ladder, so this always terminates.
  for (let i = 0; i < STYLE_ORDER.length && styleCollides(style, parentMarker, enumerated); i += 1) {
    style = childStyle(style);
  }
  if (!style) return list;

  let ordinal = 0;
  let changed = false;
  const out = list.map(f => {
    if (!f || !f.label) return f;
    const { marker, text } = splitLabelMarker(f.label);
    if (!marker) return f;                       // not enumerated — not ours to number
    ordinal += 1;
    const label = `(${formatMarker(style, ordinal)}) ${text}`.trim();
    if (label === f.label) return f;
    changed = true;
    return { ...f, label };
  });
  return changed ? out : list;
}

export default withNestedLabels;
